import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  DataTable,
  Field,
  Metric,
  Select,
  formatDate,
} from "@odingard/design-system";
import type {
  EscalationRunResult,
  ExceptionDashboard,
  ExceptionDashboardRow,
  ExceptionRoute,
} from "@odingard/sdk-client";
import type { PolicyException } from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { api, exceptionsApi, policyApi } from "../../lib/api";
import { GOVERNANCE_PERMISSIONS, GovPanel, GovernedAction } from "../../governance";
import {
  AcceptanceStateBadge,
  EXCEPTION_STATUSES,
  ExceptionStatusBadge,
  RiskLevelBadge,
  isOverdue,
  isReviewDue,
  routeModeLabel,
} from "./exceptionLabels";

type Ownership = "" | "requested" | "owned";

interface Member {
  id: string;
  email: string;
  displayName: string;
}

// Exception governance overview (APP-004 R3 P3).
//
// The dashboard is the backend's, not an aggregation done in the client:
// totals, pending approval counts and risk acceptance state all arrive
// computed, so a screen cannot quietly disagree with the record. Escalation is
// likewise a backend run. The button asks for it and reports what it raised.
export function GovExceptions() {
  const [params, setParams] = useSearchParams();
  const { activeOrg, user } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const statusFilter = params.get("status") ?? "";
  const ownership = (params.get("ownership") ?? "") as Ownership;
  const [escalation, setEscalation] = useState<EscalationRunResult | null>(null);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  const dashboard = useAsync<ExceptionDashboard>(
    async () => (await exceptionsApi.dashboard(orgId)).data.dashboard,
    [orgId],
  );
  const routes = useAsync<ExceptionRoute[]>(
    async () => (await exceptionsApi.routes(orgId)).data.routes,
    [orgId],
  );
  // Requester and risk owner live on the R2 record, so ownership filtering
  // reads them from there rather than duplicating them onto the dashboard.
  const register = useAsync<PolicyException[]>(
    async () => (await policyApi.exceptions(orgId)).data.exceptions,
    [orgId],
  );

  const members = useAsync<Member[]>(async () => (await api.members(orgId)).members, [orgId]);
  const memberName = (id: string) => {
    const match = (members.data ?? []).find((m) => m.id === id);
    return match ? match.displayName || match.email : id;
  };

  const userId = user?.id ?? "";
  const registerById = new Map((register.data ?? []).map((e) => [e.id, e]));
  const matchesOwnership = (row: ExceptionDashboardRow) => {
    if (!ownership) return true;
    const record = registerById.get(row.exceptionId);
    if (!record) return false;
    return ownership === "requested"
      ? record.requestedBy === userId
      : record.riskOwnerId === userId;
  };

  const rows = dashboard.data?.rows ?? [];
  const reviewDue = rows.filter((r) => isReviewDue(r.status, r.reviewAt)).length;
  const acceptanceAtRisk = rows.filter(
    (r) => r.riskAcceptanceState === "expired" || r.riskAcceptanceState === "revoked",
  ).length;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Exceptions</h1>
          <p>
            Every approved departure from policy, what compensates for it, who accepted the residual
            risk and when it must be looked at again.
          </p>
        </div>
        <div className="badge-row">
          <GovernedAction
            permission={GOVERNANCE_PERMISSIONS.exceptionsManage}
            label="Run escalation"
            title="Run the escalation pass"
            description="Asks the backend to raise overdue exceptions, exceptions carrying high risk, reviews that have come due and risk acceptances that are expiring."
            consequence="Escalations are raised once per exception. Running the pass again does not raise them a second time."
            confirmLabel="Run escalation"
            busyLabel="Running…"
            triggerVariant="primary"
            onConfirm={async () => {
              const res = await exceptionsApi.runEscalations(orgId);
              setEscalation(res.data.escalations);
              return res;
            }}
            onDone={dashboard.reload}
          />
        </div>
      </div>

      {escalation ? (
        <Card>
          <CardHeader title="Last escalation run" />
          <CardBody>
            <div className="metric-grid">
              <div className="metric-card">
                <Metric value={escalation.overdue} label="Overdue raised" />
              </div>
              <div className="metric-card">
                <Metric value={escalation.highRisk} label="High risk raised" />
              </div>
              <div className="metric-card">
                <Metric value={escalation.reviewDue} label="Reviews raised" />
              </div>
              <div className="metric-card">
                <Metric
                  value={escalation.riskAcceptanceExpiring}
                  label="Expiring acceptances raised"
                />
              </div>
            </div>
            <p className="muted-note">
              A repeat run reports zero for anything already escalated.
            </p>
          </CardBody>
        </Card>
      ) : null}

      <GovPanel
        title="Escalation dashboard"
        state={dashboard}
        loadingLabel="Loading the exception dashboard…"
      >
        {(data) => (
          <>
            <div className="metric-grid" data-testid="exception-totals">
              {[
                { label: "Open", value: data.totals.open },
                { label: "Approved", value: data.totals.approved },
                { label: "High or critical risk", value: data.totals.highRisk },
                { label: "Past their end date", value: data.totals.overdue },
                { label: "Review due", value: reviewDue },
                { label: "Awaiting risk acceptance", value: data.totals.awaitingRiskAcceptance },
                { label: "Acceptance expired or revoked", value: acceptanceAtRisk },
              ].map((tile) => (
                <div className="metric-card" key={tile.label}>
                  <Metric value={tile.value} label={tile.label} />
                </div>
              ))}
            </div>
            <p className="muted-note">Generated {formatDate(data.generatedAt)}.</p>
          </>
        )}
      </GovPanel>

      <Card>
        <CardBody>
          <div className="filter-row">
            <Field label="Status">
              {(id) => (
                <Select
                  id={id}
                  value={statusFilter}
                  onChange={(e) => setParam("status", e.target.value)}
                >
                  <option value="">Any status</option>
                  {EXCEPTION_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Ownership">
              {(id) => (
                <Select
                  id={id}
                  value={ownership}
                  onChange={(e) => setParam("ownership", e.target.value)}
                >
                  <option value="">Anyone</option>
                  <option value="requested">Requested by me</option>
                  <option value="owned">I own the risk</option>
                </Select>
              )}
            </Field>
          </div>
        </CardBody>
      </Card>

      <GovPanel
        title="Exceptions"
        state={dashboard}
        loadingLabel="Loading exceptions…"
        isEmpty={(d) =>
          d.rows.filter((r) => (!statusFilter || r.status === statusFilter) && matchesOwnership(r))
            .length === 0
        }
        emptyTitle="No exceptions match"
        emptyDescription="Clear the filters to see every exception on the register."
      >
        {(data) => (
          <DataTable
            caption="Exceptions"
            columns={[
              {
                key: "title",
                header: "Exception",
                render: (row) => (
                  <>
                    <Link to={`/studio/governance/exceptions/${row.id}`}>{row.title}</Link>
                    <div className="badge-row">
                      <ExceptionStatusBadge status={row.status} />
                      <RiskLevelBadge level={row.riskLevel} />
                    </div>
                  </>
                ),
              },
              {
                key: "window",
                header: "In force until",
                render: (row) => (
                  <>
                    {row.approvedEnd ? (
                      formatDate(row.approvedEnd)
                    ) : (
                      <span className="muted-note">No end date</span>
                    )}
                    {isOverdue(row.status, row.approvedEnd) ? (
                      <div className="badge-row">
                        <Badge tone="danger">Overdue</Badge>
                      </div>
                    ) : null}
                  </>
                ),
              },
              {
                key: "review",
                header: "Review",
                render: (row) => (
                  <>
                    {row.reviewAt ? (
                      formatDate(row.reviewAt)
                    ) : (
                      <span className="muted-note">Not scheduled</span>
                    )}
                    {isReviewDue(row.status, row.reviewAt) ? (
                      <div className="badge-row">
                        <Badge tone="warning">Review due</Badge>
                      </div>
                    ) : null}
                  </>
                ),
              },
              {
                key: "approvals",
                header: "Approvals",
                render: (row) =>
                  row.pendingApprovals > 0 ? (
                    <Badge tone="info">{row.pendingApprovals} outstanding</Badge>
                  ) : (
                    <Badge tone="success">Satisfied</Badge>
                  ),
              },
              {
                key: "controls",
                header: "Compensating controls",
                render: (row) => (
                  <>
                    {row.controlCount}
                    {row.ineffectiveControls > 0 ? (
                      <div className="badge-row">
                        <Badge tone="danger">{row.ineffectiveControls} ineffective</Badge>
                      </div>
                    ) : null}
                  </>
                ),
              },
              {
                key: "acceptance",
                header: "Residual risk",
                render: (row) => <AcceptanceStateBadge state={row.riskAcceptanceState} />,
              },
            ]}
            rows={data.rows
              .filter((r) => (!statusFilter || r.status === statusFilter) && matchesOwnership(r))
              .map((r) => ({ ...r, id: r.exceptionId }))}
          />
        )}
      </GovPanel>

      <GovPanel
        title="Approval routes"
        state={routes}
        loadingLabel="Loading approval routes…"
        isEmpty={(d) => d.length === 0}
        emptyTitle="No approval routes"
        emptyDescription="Without a route an exception cannot be sent for approval."
      >
        {(data) => (
          <DataTable
            caption="Approval routes"
            columns={[
              {
                key: "name",
                header: "Route",
                render: (row) => (
                  <>
                    <strong>{row.name}</strong>
                    {row.isDefault ? (
                      <div className="badge-row">
                        <Badge tone="success">Default</Badge>
                      </div>
                    ) : null}
                    {row.description ? <div className="muted-note">{row.description}</div> : null}
                  </>
                ),
              },
              { key: "mode", header: "Mode", render: (row) => routeModeLabel(row.mode) },
              {
                key: "approvers",
                header: "Approvers",
                render: (row) => (
                  <>
                    {row.steps.length === 0 ? (
                      <span className="muted-note">None configured</span>
                    ) : (
                      row.steps
                        .map((step) => memberName(step.approverId))
                        .join(row.mode === "sequential" ? " \u2192 " : " \u00b7 ")
                    )}
                    <div className="muted-note">
                      {row.requiredApprovals} of {row.steps.length} required
                      {row.requiresRiskAcceptance ? " · risk acceptance required first" : ""}
                    </div>
                  </>
                ),
              },
              {
                key: "actions",
                header: "",
                render: (row) =>
                  row.isDefault ? null : (
                    <GovernedAction
                      permission={GOVERNANCE_PERMISSIONS.exceptionsManage}
                      label="Make default"
                      title="Make this the default route"
                      description="Exceptions with no route of their own will follow this one."
                      consequence="Exceptions already partway through another route keep the route they started on."
                      confirmLabel="Make default"
                      busyLabel="Saving…"
                      onConfirm={() => exceptionsApi.setDefaultRoute(orgId, row.id)}
                      onDone={routes.reload}
                    />
                  ),
              },
            ]}
            rows={data}
          />
        )}
      </GovPanel>
    </>
  );
}
